import {Box, FormControlLabel, Typography} from '@mui/material';
import React, {useCallback, useMemo} from 'react';
import AurServerSelect from '../../common/aur-select-dropdown/AurServerSelect';
import {t} from 'i18next';
import AurCheckbox from '../../common/aur-checkbox/AurCheckbox';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import {CustodianModal} from '../../../hooks/index-search/useCustodians';

export type CustodiansQueryConditionType = 'OR' | 'AND';
export interface CustodiansDropdownProps {
  isLoading?: boolean;
  custodians: CustodianModal[];
  selectedCustodians: CustodianModal[];
  queryConditionType: CustodiansQueryConditionType;
  setSelectedCustodians: React.Dispatch<React.SetStateAction<CustodianModal[]>>;
  setQueryConditionType: React.Dispatch<
    React.SetStateAction<CustodiansQueryConditionType>
  >;
  onSearch?: (searchTerm: string) => Promise<CustodianModal[] | undefined>;
  onApplyClick?: (
    selectedCustodians: CustodianModal[],
    queryConditionType: string
  ) => void;
  onOpenDropdown?: () => void;
}

const CustodiansDropdown: React.FC<CustodiansDropdownProps> = ({
  isLoading,
  custodians,
  selectedCustodians,
  queryConditionType,
  setSelectedCustodians,
  setQueryConditionType,
  onSearch,
  onApplyClick,
  onOpenDropdown,
}) => {
  const onSelectedItems = useCallback(
    (items?: CustodianModal[]) => {
      setSelectedCustodians(items ?? []);
      onApplyClick?.(items ?? [], queryConditionType);
    },
    [setSelectedCustodians, onApplyClick, queryConditionType]
  );

  const onClearClicked = useCallback(() => {
    setQueryConditionType('OR');
    setSelectedCustodians([]);
  }, [setSelectedCustodians, setQueryConditionType]);

  const renderValue = useCallback(() => {
    return (
      <Box className={'dropdown-input-value'} tabIndex={0}>
        <AurIcon icon={IconNames.GroupOutlined} />
        <Typography className="typo-text body2_size">
          {t('ecaHomepageModals.ecaBottom.allProcessedCustodians')}
        </Typography>
      </Box>
    );
  }, [selectedCustodians, custodians]);

  const renderCheckbox = useCallback(
    (type: CustodiansQueryConditionType, title?: string) => {
      return (
        <FormControlLabel
          control={
            <AurCheckbox
              checked={queryConditionType === type}
              onChange={() => setQueryConditionType(type)}
            />
          }
          label={
            <Typography className="typo-text body3_size checkbox-label">
              {title}
            </Typography>
          }
          className="checkbox-container"
        />
      );
    },
    [queryConditionType, setQueryConditionType]
  );

  const renderFooter = useMemo(() => {
    return (
      <Box className={'footer-checkbox-container'}>
        {renderCheckbox(
          'OR',
          t('indexSearchPage.searchEachCustodianIndividuallyOR')
        )}
        {renderCheckbox('AND', t('indexSearchPage.searchBetweenCustodiansAND'))}
      </Box>
    );
  }, [renderCheckbox]);

  return (
    <AurServerSelect
      dataTestId={'custodian-dropdown'}
      menuClass="custodian-dropdown"
      className="dropdown-input dropdown-input-focus MuiPaper-root custodian-dropdown"
      selectAllLabel={t('indexSearchPage.selectAll')}
      dismissDropdownWhenClear={true}
      displayEmpty={true}
      renderValue={renderValue}
      renderFooter={renderFooter}
      items={custodians as never}
      isLoading={isLoading}
      labelId={'custodian'}
      itemDisplay="name"
      itemKey="key"
      onSelectionChange={onSelectedItems as never}
      onClearClicked={onClearClicked}
      onSearch={onSearch as never}
      defaultSelection={selectedCustodians as never}
      showCheckIcon={false}
      itemHeight={36}
      numberOfVisibleItem={9.5}
      maxDropdownItemLength={90}
      itemsCountwithNoScroll={10}
      itemsPerPage={25}
      hasNextPage={false}
      onOpenDropdown={() => {
        onOpenDropdown?.();
      }}
    />
  );
};

export {CustodiansDropdown};
